'use client';

import { ComponentProps } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { XIcon } from 'lucide-react';

import { cn } from '../lib';

/**
 * Root container for a dialog, based on Radix UI's Dialog primitive.
 */
function Dialog({ ...props }: ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

/**
 * The element that triggers the dialog to open.
 */
function DialogTrigger({
  ...props
}: ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({
  ...props
}: ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({
  ...props
}: ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

/**
 * The overlay that appears behind the dialog content.
 */
function DialogOverlay({
  className,
  ...props
}: ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      className={cn(
        `data-[state=open]:animate-in data-[state=closed]:animate-out
        data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0
        z-50 bg-black/60`,
        className
      )}
      data-slot="dialog-overlay"
      {...props}
    />
  );
}

/**
 * The content container for the dialog.
 */
function DialogContent({
  className,
  classNameOverlay,
  children,
  showCloseButton = true,
  ...props
}: ComponentProps<typeof DialogPrimitive.Content> & {
  /** Whether to show the close button. */
  showCloseButton?: boolean;
  /** Optional CSS class name for the overlay. */
  classNameOverlay?: string;
}) {
  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay className={classNameOverlay} />
      <DialogPrimitive.Content
        className={cn(
          `bg-background data-[state=open]:animate-in data-[state=closed]:animate-out
          data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0
          data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed
          left-[50%] top-[50%] z-50 grid w-full max-w-[calc(100%-2rem)]
          translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border p-6
          shadow-lg duration-200 sm:max-w-lg`,
          className
        )}
        data-slot="dialog-content"
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            className="
              ring-offset-background focus:ring-ring data-[state=open]:bg-accent
              data-[state=open]:text-muted-foreground rounded-xs focus:outline-hidden
              [&_svg:not([class*='size-'])]:size-4 absolute right-4 top-4 opacity-70
              transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2
              disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0
            "
            data-slot="dialog-close"
          >
            <XIcon />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

/**
 * The header section of the dialog.
 */
function DialogHeader({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      className={cn('flex flex-col gap-2 text-center sm:text-start', className)}
      data-slot="dialog-header"
      {...props}
    />
  );
}

/**
 * The footer section of the dialog.
 */
function DialogFooter({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'flex flex-col-reverse gap-2 sm:flex-row sm:justify-end',
        className
      )}
      data-slot="dialog-footer"
      {...props}
    />
  );
}

/**
 * The title of the dialog.
 */
function DialogTitle({
  className,
  ...props
}: ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      className={cn('text-lg font-semibold leading-none', className)}
      data-slot="dialog-title"
      {...props}
    />
  );
}

/**
 * A brief description of the dialog's purpose.
 */
function DialogDescription({
  className,
  ...props
}: ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      className={cn('text-muted-foreground text-sm', className)}
      data-slot="dialog-description"
      {...props}
    />
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
